import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Snackbar,
  AlertColor
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import QuickSaleUISettingsModal from './modals/QuickSaleUISettingsModal';
import PendingSalesModal, { PendingSale } from './modals/PendingSalesModal';
import PartialPaymentModal, { PaymentDetails } from './modals/PartialPaymentModal';
import WeightInputModal from './WeightInputModal';
import BulkAddAccountModal from '../../../accounting/accounts/BulkAddAccountModal';
import CustomerSelectionModal from './CustomerSelectionModal';
import QuickAddItemModal from '../../../accounting/inventory/QuickAddItemModal';
import PaymentWaitingModal from './PaymentWaitingModal';
import { CartItem } from '../QuickSaleScreen';
import { IngenicoOpenTransactionData } from '../hooks/fiscalDevices/ingenico';

// Alert componentini Snackbar içinde kullanmak için özelleştirelim
const AlertSnackbar = React.forwardRef<HTMLDivElement, AlertProps>(function AlertSnackbar(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

interface QuickSaleModalsProps {
  // Alert Modal
  alertModalOpen: boolean;
  alertModalTitle: string;
  alertModalMessage: string;
  isCriticalError: boolean;
  showQuickAddButton: boolean;
  alertModalRetryCallback?: (() => void) | null;
  handleCloseAlertModal: () => void;
  handleCriticalErrorClearCart: () => void;
  setIsQuickAddItemModalOpen: (open: boolean) => void;
  currentUserPermissions: any;
  quickSaleUserData: any;
  loadingUserPermissions: boolean;
  
  // UI Settings Modal
  isUISettingsModalOpen: boolean;
  setIsUISettingsModalOpen: (open: boolean) => void;
  productCardSize: number;
  productCardFontSize: number;
  productCategoryTabSize: number;
  productImageSize: number;
  controlPanelPosition: 'left' | 'right';
  showProductImages: boolean;
  showProductPrices: boolean;
  cartColumnWidth: number;
  showAllCategory: boolean;
  showSearchBar: boolean;
  categoryLayout: 'horizontal' | 'vertical';
  categoryPosition: 'left' | 'right';
  categoryFontSize: number; // ✅ YENİ: Kategori yazı boyutu
  useFiscalDeviceForCash?: boolean;
  onUseFiscalDeviceForCashChange?: (enabled: boolean) => void;
  handleSaveUISettings: (newSettings: { cardSize: number; fontSize: number; categoryTabSize: number; imageSize: number; controlPanelPosition: 'left' | 'right'; showProductImages?: boolean; showProductPrices?: boolean; cartColumnWidth?: number; showAllCategory?: boolean; showSearchBar?: boolean; categoryLayout?: 'horizontal' | 'vertical'; categoryPosition?: 'left' | 'right'; categoryFontSize?: number }) => void;
  // Yazdırma ayarları için ek prop'lar
  uiSettingsScreenId?: number | null;
  uiSettingsCompanyId?: string | null;
  uiSettingsScreenSettings?: any;
  uiSettingsDisplayableCategories?: any[];
  uiSettingsIsRestaurantMode?: boolean;
  uiSettingsOnPrintingSettingsSaved?: () => void;
  
  // Pending Sales Modal
  isPendingSalesModalOpen: boolean;
  setIsPendingSalesModalOpen: (open: boolean) => void;
  pendingSales: PendingSale[];
  handleRestoreSale: (saleId: number) => void;
  handleDeleteSale: (saleId: number) => void;
  
  // Partial Payment Modal
  isPartialPaymentModalOpen: boolean;
  handleClosePartialPaymentModal: (paymentDetails: PaymentDetails | null) => void;
  calculateTotal: () => number;
  companyId: string | null;
  currentScreen: any;
  cashPaymentLabel: string;
  cashPaymentCashboxId: string | null;
  posOptions: any[];
  openAccountPaymentOption: any;
  selectedCustomer: any;
  cart: CartItem[];
  selectedFiscalDevice: 'hugin_t300' | 'inpos_m530' | 'beko_x30tr' | 'beko_300tr' | 'pavopay' | 'ingenico_5000f';
  inventoryItems: any[];
  showAlertModal: (title: string, message: string, showQuickAdd?: boolean, onRetry?: (() => void) | null) => void;
  setIsProcessingPosPayment: (processing: boolean) => void; // ✅ Mavi modal için
  isRestaurantMode?: boolean; // ✅ YENİ: Restoran modunda fiyatlar zaten KDV dahil
  ingenicoRecoveryData?: IngenicoOpenTransactionData | null;
  useExternalPrinterForPavo?: boolean;
  kitchenOrderCode?: string;
  receiptCompanyName?: string;
  // 🎫 NUMARA GARANTİSİ: Parçalı ödemede cihaz denemesi yapıldıktan sonra satış
  // tamamlanmadan modal kapatılırsa sipariş numarasını yenilemek için.
  onPartialPaymentAttemptFailed?: () => void;
  
  // Discount Modal
  isDiscountModalOpen: boolean;
  handleCloseDiscountModal: () => void;
  discountInputPercentage: string;
  setDiscountInputPercentage: (value: string) => void;
  discountInputAmount: string;
  setDiscountInputAmount: (value: string) => void;
  handleRemoveDiscount: () => void;
  lastAppliedDiscountAmount: number;
  appliedDiscountType: 'percentage' | 'amount' | null;
  handleApplyDiscount: () => void;
  /** Hızlı yüzde butonları (%10/%20): tıklandığı anda indirimi uygular, Uygula gerektirmez. */
  handleApplyQuickDiscount: (percent: number) => void;
  
  // Weight Input Modal
  isWeightInputModalOpen: boolean;
  setIsWeightInputModalOpen: (open: boolean) => void;
  isCartUpdateMode: boolean;
  setIsCartUpdateMode: (mode: boolean) => void;
  setSelectedCartItem: (item: CartItem | null) => void;
  handleQuantityUpdateConfirm: (quantity: number, mode: 'add' | 'update') => void;
  tempSelectedProduct: any;
  addToCart: (product: any, quantity: number) => void;
  
  // Bulk Add Account Modal
  isBulkAddAccountModalOpen: boolean;
  setIsBulkAddAccountModalOpen: (open: boolean) => void;
  refreshCustomers: () => Promise<any>;
  
  // Snackbar
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: AlertColor;
  handleSnackbarClose: (event?: React.SyntheticEvent | Event, reason?: string) => void;
  
  // Customer Selection Modal
  isCustomerSelectionModalOpen: boolean;
  handleCloseCustomerSelectionModal: () => void;
  handleSelectCustomerFromModalWrapper: (customer: any) => void;
  handleOpenAddAccountModal?: () => void; // ✅ YENİ: Yeni cari ekleme modalını açma
  
  // Quick Add Item Modal
  isQuickAddItemModalOpen: boolean;
  handleCloseQuickAddItemModal: () => void;
  
  // Payment Waiting Modal
  paymentWaitingModalOpen: boolean;
  paymentWaitingStatus: 'waiting' | 'success' | 'error';
  paymentWaitingMessage: string;
}

const QuickSaleModals: React.FC<QuickSaleModalsProps> = ({
  // Alert Modal
  alertModalOpen,
  alertModalTitle,
  alertModalMessage,
  isCriticalError,
  showQuickAddButton,
  alertModalRetryCallback,
  handleCloseAlertModal,
  handleCriticalErrorClearCart,
  setIsQuickAddItemModalOpen,
  currentUserPermissions,
  quickSaleUserData,
  loadingUserPermissions,
  
  // UI Settings Modal
  isUISettingsModalOpen,
  setIsUISettingsModalOpen,
  productCardSize,
  productCardFontSize,
  productCategoryTabSize,
  productImageSize,
  controlPanelPosition,
  showProductImages,
  showProductPrices,
  cartColumnWidth,
  showAllCategory,
  showSearchBar,
  categoryLayout,
  categoryPosition,
  categoryFontSize, // ✅ YENİ
  useFiscalDeviceForCash,
  onUseFiscalDeviceForCashChange,
  handleSaveUISettings,
  uiSettingsScreenId,
  uiSettingsCompanyId,
  uiSettingsScreenSettings,
  uiSettingsDisplayableCategories,
  uiSettingsIsRestaurantMode,
  uiSettingsOnPrintingSettingsSaved,
  
  // Pending Sales Modal
  isPendingSalesModalOpen,
  setIsPendingSalesModalOpen,
  pendingSales,
  handleRestoreSale,
  handleDeleteSale,
  
  // Partial Payment Modal
  isPartialPaymentModalOpen,
  handleClosePartialPaymentModal,
  calculateTotal,
  companyId,
  currentScreen,
  cashPaymentLabel,
  cashPaymentCashboxId,
  posOptions,
  openAccountPaymentOption,
  selectedCustomer,
  cart,
  selectedFiscalDevice,
  inventoryItems,
  showAlertModal,
  setIsProcessingPosPayment, // ✅ Mavi modal için
  isRestaurantMode = false, // ✅ YENİ: Restoran modunda fiyatlar zaten KDV dahil
  ingenicoRecoveryData,
  useExternalPrinterForPavo = false,
  kitchenOrderCode,
  receiptCompanyName,
  onPartialPaymentAttemptFailed,
  
  // Discount Modal
  isDiscountModalOpen,
  handleCloseDiscountModal,
  discountInputPercentage,
  setDiscountInputPercentage,
  discountInputAmount,
  setDiscountInputAmount,
  handleRemoveDiscount,
  lastAppliedDiscountAmount,
  appliedDiscountType,
  handleApplyDiscount,
  handleApplyQuickDiscount,
  
  // Weight Input Modal
  isWeightInputModalOpen,
  setIsWeightInputModalOpen,
  isCartUpdateMode,
  setIsCartUpdateMode,
  setSelectedCartItem,
  handleQuantityUpdateConfirm,
  tempSelectedProduct,
  addToCart,
  
  // Bulk Add Account Modal
  isBulkAddAccountModalOpen,
  setIsBulkAddAccountModalOpen,
  refreshCustomers,
  
  // Snackbar
  snackbarOpen,
  snackbarMessage,
  snackbarSeverity,
  handleSnackbarClose,
  
  // Customer Selection Modal
  isCustomerSelectionModalOpen,
  handleCloseCustomerSelectionModal,
  handleSelectCustomerFromModalWrapper,
  handleOpenAddAccountModal, // ✅ YENİ
  
  // Quick Add Item Modal
  isQuickAddItemModalOpen,
  handleCloseQuickAddItemModal,
  
  // Payment Waiting Modal
  paymentWaitingModalOpen,
  paymentWaitingStatus,
  paymentWaitingMessage,
}) => {
  return (
    <>
      {/* Uyarı Modalı (Dialog - Hatalar veya özel durumlar için) */}
      <Dialog
        open={alertModalOpen}
        onClose={alertModalRetryCallback ? undefined : handleCloseAlertModal}
        disableEscapeKeyDown={!!alertModalRetryCallback || isCriticalError}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth={isCriticalError ? "md" : "sm"}
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">{alertModalTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ whiteSpace: 'pre-line' }}>
            {alertModalMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexDirection: (isCriticalError || alertModalRetryCallback) ? 'column' : 'row', gap: 1 }}>
          {isCriticalError && (
            <>
              <Box sx={{ display: 'flex', gap: 1, width: '100%', mb: 1 }}>
                <Button 
                  onClick={handleCriticalErrorClearCart}
                  variant="contained"
                  color="error"
                  fullWidth
                  startIcon={<DeleteIcon />}
                >
                  Sepeti Temizle (Güvenlik)
                </Button>
                <Button 
                  onClick={handleCloseAlertModal}
                  variant="outlined"
                  color="warning"
                  fullWidth
                >
                  Sepeti Koru (Riskli)
                </Button>
              </Box>
              <Typography variant="caption" sx={{ textAlign: 'center', fontStyle: 'italic', mb: 1 }}>
                Kritik hata durumunda sepeti temizlemeniz önerilir
              </Typography>
            </>
          )}
          {alertModalRetryCallback && !isCriticalError ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', p: 1 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  const retryFn = alertModalRetryCallback;
                  handleCloseAlertModal();
                  retryFn();
                }}
                sx={{
                  bgcolor: 'success.main',
                  '&:hover': { bgcolor: 'success.dark' },
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                Yeniden Dene (Ödeme Kaydı)
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<CloseIcon />}
                onClick={handleCloseAlertModal}
                sx={{ py: 1.5, fontSize: 16, fontWeight: 600 }}
              >
                Kapat
              </Button>
            </Box>
          ) : !isCriticalError && (
            <Box sx={{ display: 'flex', gap: 1, width: 'auto' }}>
              {showQuickAddButton && (
                <Button 
                  onClick={() => {
                    console.log("[QuickSaleModals] Hızlı Stok Kaydı Buton Tıklandı. Yetkiler:", { 
                      canQuickAddItem: currentUserPermissions?.canQuickAddItem, 
                      isAdmin: quickSaleUserData?.role === 'admin', 
                      isLoading: loadingUserPermissions 
                    });
                    if (currentUserPermissions?.canQuickAddItem || quickSaleUserData?.role === 'admin') {
                      setIsQuickAddItemModalOpen(true);
                    }
                  }}
                  disabled={!(currentUserPermissions?.canQuickAddItem || quickSaleUserData?.role === 'admin') || loadingUserPermissions}
                  startIcon={<PlaylistAddIcon />} 
                  variant="outlined" 
                  sx={{ mr: 1 }}
                >
                  Hızlı Stok Kart Kaydı
                </Button>
              )}
              <Button onClick={handleCloseAlertModal} autoFocus variant="contained">
                Tamam
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* UI Ayarları Modalı */}
      <QuickSaleUISettingsModal
        open={isUISettingsModalOpen}
        onClose={() => setIsUISettingsModalOpen(false)}
        currentCardSize={productCardSize} 
        currentFontSize={productCardFontSize} 
        currentCategoryTabSize={productCategoryTabSize}
        currentImageSize={productImageSize}
        currentControlPanelPosition={controlPanelPosition}
        currentShowProductImages={showProductImages}
        currentShowProductPrices={showProductPrices}
        currentCartColumnWidth={cartColumnWidth}
        currentShowAllCategory={showAllCategory}
        currentShowSearchBar={showSearchBar}
        currentCategoryLayout={categoryLayout}
        currentCategoryPosition={categoryPosition}
        currentCategoryFontSize={categoryFontSize}
        currentUseFiscalDeviceForCash={useFiscalDeviceForCash}
        onUseFiscalDeviceForCashChange={onUseFiscalDeviceForCashChange}
        onSaveUISettings={handleSaveUISettings}
        screenId={uiSettingsScreenId}
        companyId={uiSettingsCompanyId}
        screenSettings={uiSettingsScreenSettings}
        displayableCategories={uiSettingsDisplayableCategories}
        isRestaurantMode={uiSettingsIsRestaurantMode}
        onPrintingSettingsSaved={uiSettingsOnPrintingSettingsSaved}
      />

      {/* Bekleyen Satışlar Modalı */}
      <PendingSalesModal
        open={isPendingSalesModalOpen}
        onClose={() => setIsPendingSalesModalOpen(false)}
        pendingSales={pendingSales}
        onRestoreSale={handleRestoreSale}
        onDeleteSale={handleDeleteSale}
      />

      {/* Parçalı Ödeme Modalı */}
      <PartialPaymentModal
        open={isPartialPaymentModalOpen}
        onClose={handleClosePartialPaymentModal}
        totalAmount={calculateTotal()} 
        companyId={companyId} 
        screenId={currentScreen?.id || null}
        cashOptionLabel={cashPaymentLabel}
        cashOptionCashboxId={cashPaymentCashboxId}
        posOptionsList={posOptions} 
        openAccountOption={openAccountPaymentOption} 
        isGeneralCustomer={selectedCustomer?.companyName === 'Genel Cari'}
        cart={cart}
        selectedFiscalDevice={selectedFiscalDevice}
        inventoryItems={inventoryItems}
        showAlertModal={showAlertModal}
        setIsProcessingPosPayment={setIsProcessingPosPayment}
        isRestaurantMode={isRestaurantMode}
        ingenicoRecoveryData={ingenicoRecoveryData}
        useExternalPrinterForPavo={useExternalPrinterForPavo}
        kitchenOrderCode={kitchenOrderCode}
        receiptCompanyName={receiptCompanyName}
        isRestP2PEnabled={Boolean(uiSettingsScreenSettings?.useRestP2PForPayments && uiSettingsScreenSettings?.pavoRestP2PDeviceScreenCode)}
        restP2PDeviceCode={uiSettingsScreenSettings?.pavoRestP2PDeviceScreenCode || null}
        restP2PDeviceName={uiSettingsScreenSettings?.pavoRestP2PDeviceName || null}
        onPaymentAttemptFailed={onPartialPaymentAttemptFailed}
      />

      {/* İndirim Modalı */}
      <Dialog open={isDiscountModalOpen} onClose={handleCloseDiscountModal} maxWidth="xs" fullWidth>
        <DialogTitle>İndirim Uygula</DialogTitle>
        <DialogContent>
          {/* ✅ YENİ: KDV Dahil fiyatlara indirim uygulanır bilgisi */}
          <Box 
            sx={{ 
              p: 1.5, 
              mb: 2,
              bgcolor: 'info.lighter', 
              borderRadius: 1, 
              border: '1px solid',
              borderColor: 'info.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography variant="body2" color="info.main" sx={{ fontWeight: 500 }}>
              ℹ️ KDV Dahil fiyatlara indirim uygulanır
            </Typography>
          </Box>

          {/* Hızlı yüzde indirim butonu: tıklandığı anda uygular (Uygula gerekmez) */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              color="success"
              fullWidth
              onClick={() => handleApplyQuickDiscount(10)}
              sx={{ fontWeight: 700 }}
            >
              %10 İndirim
            </Button>
          </Box>

          <TextField
            margin="dense"
            id="discount-percentage"
            label="İndirim Yüzdesi (%)"
            type="number"
            fullWidth
            variant="outlined"
            value={discountInputPercentage}
            onChange={(e) => setDiscountInputPercentage(e.target.value)}
            InputProps={{
              inputProps: { 
                min: 0.01, 
                max: 100,
                step: "0.01"
              }
            }}
            sx={{mb: 2}}
          />
          <TextField
            margin="dense"
            id="discount-amount"
            label="İndirim Tutarı (TL)"
            type="number"
            fullWidth
            variant="outlined"
            value={discountInputAmount}
            onChange={(e) => setDiscountInputAmount(e.target.value)}
            InputProps={{
              inputProps: { 
                min: 0.01,
                step: "0.01"
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{justifyContent: 'space-between', px: 2.5, py:1.5}}>
          <Button onClick={handleRemoveDiscount} color="error" disabled={lastAppliedDiscountAmount === 0 && appliedDiscountType === null}>
            İndirimi Sil
          </Button>
          <Box>
            <Button onClick={handleCloseDiscountModal} sx={{mr:1}}>İptal</Button>
            <Button onClick={handleApplyDiscount} variant="contained">Uygula</Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* KG Ürün Ağırlık Girişi Modalı */}
      <WeightInputModal
        open={isWeightInputModalOpen}
        onClose={() => {
          console.log('WeightInputModal kapatılıyor...');
          setIsWeightInputModalOpen(false);
          setIsCartUpdateMode(false);
          setSelectedCartItem(null);
        }}
        onConfirm={isCartUpdateMode 
          ? handleQuantityUpdateConfirm 
          : (weight, mode) => {
              console.log('Ağırlık girişi onaylandı:', weight);
              if (tempSelectedProduct) {
                addToCart(tempSelectedProduct.productInfo, weight);
              }
              setIsWeightInputModalOpen(false);
            }
        }
        selectedProduct={tempSelectedProduct}
        isCartUpdate={isCartUpdateMode}
      />

      {/* Toplu Cari Kayıt Modalı */}
      <BulkAddAccountModal
        open={isBulkAddAccountModalOpen}
        onClose={() => setIsBulkAddAccountModalOpen(false)}
        onAccountsAdded={refreshCustomers}
      />

      {/* Snackbar Bildirimi - Header'da göster */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }} 
      >
        <AlertSnackbar onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </AlertSnackbar>
      </Snackbar>

      {/* Müşteri Seçim Modalı */}
      <CustomerSelectionModal 
        open={isCustomerSelectionModalOpen}
        onClose={handleCloseCustomerSelectionModal}
        onSelectCustomer={handleSelectCustomerFromModalWrapper}
        companyId={companyId}
        onOpenAddAccount={handleOpenAddAccountModal} // ✅ YENİ: Yeni cari ekleme butonu
      />

      {/* Hızlı Stok Kaydı Modalı */}
      <QuickAddItemModal 
        open={isQuickAddItemModalOpen}
        onClose={handleCloseQuickAddItemModal}
        branchId={quickSaleUserData?.branchesId || undefined}
      />

      {/* Ödeme Bekleme Modalı */}
      <PaymentWaitingModal
        open={paymentWaitingModalOpen}
        status={paymentWaitingStatus}
        message={paymentWaitingMessage}
      />
    </>
  );
};

export default QuickSaleModals;

